import { Injectable, RendererFactory2, inject } from '@angular/core';
import { MenuItem } from '@models/menu';

import { BaseRestrictions } from '@models/module/fields/enums';
import { MimeTypesDict, ThumbnailsDict } from '@models/module/fields/main';
import { TranslateService } from '@ngx-translate/core';
import { DisplayFormats, StatusColumnType, TableColumn, TableRow } from '@shared/components/data-list/common';
import { PRINT_TABLE } from '@shared/consts';
import { ItvDateTimeWithRestrictionsPipe } from '@shared/pipes/itv-date.pipe';
import { ItvNumberWithFormattingsPipe } from '@shared/pipes/itv-number.pipe';
import { TranslateInsensitivePipe } from '@shared/pipes/translate-insensitive.pipe';
import { TenantStore } from '@signal-store/tenant.store';
import { UserStore } from '@signal-store/user.store';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  private userStore = inject(UserStore);
  private tenantStore = inject(TenantStore);
  private dateTimePipe = inject(ItvDateTimeWithRestrictionsPipe);
  private numberPipe = inject(ItvNumberWithFormattingsPipe);
  private translateInsensitivePipe = inject(TranslateInsensitivePipe);
  private rendererFactory = inject(RendererFactory2);
  private translate = inject(TranslateService);
  private renderer = this.rendererFactory.createRenderer(null, null);

  createTable(columns: TableColumn[], rows: TableRow[]) {
    const tableHeader = columns.map(column => {
      return `<th styles="font-size: 11px">${column.displayName}</th>`;
    });

    const getRowCells = (row: TableRow) => columns.map(column => `<td>${this.getCellDataFormatted(row, column)}</td>`);
    const tableRows = rows.map(rowData => `<tr'>${getRowCells(rowData).join('')}</tr>`);

    const table = `
    <table class='cipo-table'>
        <thead>
            <tr>${tableHeader.join('')}</tr>
        </thead>
        <tbody>
            ${tableRows.join('')}
        </tbody>
    </table>
    `;

    return table;
  }

  printTable(columns: TableColumn[], rows: TableRow[]): void {
    // Create an iframe dynamically
    const iframe = this.renderer.createElement('iframe');
    const iframeStyles = {
      width: '0px',
      height: '0px',
      border: 'none',
      display: 'none',
    };
    Object.entries(iframeStyles).forEach(([key, value]) => this.renderer.setStyle(iframe, key, value));

    this.renderer.appendChild(document.body, iframe);

    // Wait for iframe to be ready
    iframe.onload = () => {
      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(`
            <html>
                ${PRINT_TABLE.HEAD}
                <body>
                    ${this.getTablePrintHeader()}
                    ${this.createTable(columns, rows)}
                </body>
            </html>
            `);
        doc.close();

        // Trigger print after content is loaded
        setTimeout(() => {
          iframe.contentWindow?.print();
          this.renderer.removeChild(document.body, iframe); // Clean up after printing
        }, 500);
      }
    };

    // Load a blank page inside iframe
    iframe.src = 'about:blank';
  }

  printHtml(htmlContent: string) {
    const printSectionId = `section-to-print-${crypto.randomUUID()}`;
    let printSection = document.getElementById(printSectionId);

    if (printSection) {
      printSection.remove();
    }

    printSection = this.renderer.createElement('div');
    this.renderer.setProperty(printSection, 'id', printSectionId);
    this.renderer.setProperty(printSection, 'innerHTML', htmlContent);
    this.renderer.appendChild(document.body, printSection);

    setTimeout(() => {
      window.print();

      if (printSection) {
        this.renderer.removeChild(document.body, printSection);
      }
    }, 400);
  }

  private getTablePrintHeader(currentModule?: MenuItem) {
    const module = currentModule || this.userStore.menu.activeMenuItem();
    const contract = this.userStore.contract();
    const tenantDescription = this.tenantStore.selectedTenant().description;
    const today = this.dateTimePipe.transform(new Date(), BaseRestrictions.Date);
    return `
      <table class='page-header'>
        <tr>
            <td style='width: 25%;'>${tenantDescription}</td>
            <td class='text-center'${module.perContract ? " rowspan='2'" : ''}'>
                <h3>${module.name}</h3>
            </td>
            <td class='text-right' style='width: 25%;'>${module.perContract ? contract.name : today}</td>
        </tr>
        ${
          module.perContract
            ? `
            <tr>
                <td>${contract.no}</td>
                <td class='text-right'>${today}</td>
            </tr>`
            : ''
        }
      </table>`;
  }

  private getCellDataFormatted(row: TableRow, column: TableColumn) {
    const data = row[column.name];
    if (data === undefined || data === null) {
      return '-';
    }
    switch (column.displayFormatId) {
      case DisplayFormats.Date:
        return this.dateTimePipe.transform(data as string, column.restrictions, '-');
      case DisplayFormats.Number:
        return this.numberPipe.transform(
          data as number,
          column.formattings,
          column.restrictions,
          this.userStore.currency(),
        );
      case DisplayFormats.Image:
        if (column.showThumbnail) {
          const mimeType = this.getMimeType(data as string);
          let src = '';
          if (row[column.name + '_thumb']) {
            src = `data:${mimeType};base64, ${row[column.name + '_thumb']}`;
          } else {
            const srcPath: string = ThumbnailsDict[mimeType] || ThumbnailsDict.default;
            src = srcPath.substring(3); // Remove 'src' from the path
          }
          return PRINT_TABLE.getImageThumb(src);
        }
        return data;
      case DisplayFormats.Status:
        const status: StatusColumnType = {
          ...(data as StatusColumnType),
          label: this.translate.instant((data as StatusColumnType).label),
        };
        return PRINT_TABLE.getStateElement(status);
      case DisplayFormats.YesNo:
        return this.translateInsensitivePipe.transform(`general.boolValue.${data}`);
      case DisplayFormats.TranslateKey:
        return this.translate.instant(data as string);
      case DisplayFormats.IconText:
        return (data as { text?: string })?.text ?? '';
      case DisplayFormats.Text:
        if ((data as string)?.length > 100) {
          return (data as string).slice(0, 100) + '...';
        }
        return data;
      default:
        return data;
    }
  }

  private getMimeType(filenames: string) {
    if (!filenames) {
      return null;
    }
    const filename = filenames.split(',')[0];
    const extension = filename.split('.').pop();
    return MimeTypesDict[extension.trim()];
  }
}
